import { useAuth } from '@/app/auth';
import { env } from './env';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { token, signOut } = useAuth.getState();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Token ${token}`);

  const res = await fetch(`${env.apiBase}${path}`, { ...options, headers });

  if (res.status === 401) signOut(); // token rejected → drop it; guards route to /login

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(res.statusText, data), data);
  }
  return data as T;
}

/**
 * Pull a human-readable reason out of a DRF error body.
 *
 * This used to read `detail` only, which covers DRF's generic errors but NOT
 * its field errors — those are shaped `{"username": ["That name is
 * reserved."]}` with no `detail` key at all. Every one of them degraded to
 * `res.statusText`, so the UI could only ever say "Bad Request", and a
 * validation rule the user cannot read the violation of is worse than no rule.
 *
 * Order matters: `detail` first (the explicit message when DRF provides one),
 * then the first field error. Falls back to the status text so the caller
 * always gets something printable.
 */
export function errorMessage(fallback: string, body: unknown): string {
  if (!body || typeof body !== 'object') return fallback;
  const obj = body as Record<string, unknown>;

  if (typeof obj.detail === 'string') return obj.detail;

  for (const value of Object.values(obj)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return fallback;
}

/** The field errors from a DRF 400, as `{field: message}`. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(err.body as Record<string, unknown>)) {
    if (key === 'detail') continue;
    if (typeof value === 'string') out[key] = value;
    else if (Array.isArray(value) && typeof value[0] === 'string') out[key] = value[0];
  }
  return out;
}

export interface HeadlessBody {
  status?: number;
  meta?: { session_token?: string };
  errors?: { message: string }[];
}

export interface HeadlessResponse {
  status: number;
  ok: boolean;
  data: HeadlessBody;
}

/**
 * allauth headless app-client call. Unlike `api`, this does NOT attach the DRF
 * token, does NOT sign out on 401 (the headless flow uses 401 as "flow
 * pending" and still returns a useful body), and carries the flow's session
 * token via the `X-Session-Token` header. Returns body + status so callers can
 * read `meta.session_token`.
 */
export async function headlessPost(
  path: string,
  body: unknown,
  sessionToken?: string | null,
): Promise<HeadlessResponse> {
  const headers = new Headers({ Accept: 'application/json', 'Content-Type': 'application/json' });
  if (sessionToken) headers.set('X-Session-Token', sessionToken);
  const res = await fetch(`${env.apiBase}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  const text = await res.text();
  let data: HeadlessBody = {};
  if (text) {
    try {
      data = JSON.parse(text) as HeadlessBody;
    } catch {
      data = {};
    }
  }
  return { status: res.status, ok: res.ok, data };
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
